// react
import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui icons
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';

// material ui
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

// feature-components
import FaqQuestion from '@/features/faq/components/FaqQuestion';
import FaqQuestionSectionWrapper from '@/features/faq/components/FaqQuestionSectionWrapper';
import FaqServiceSectionWrapper from '@/features/faq/components/FaqServiceSectionWrapper';
import { QuestionsList } from '@/features/faq/components/QuestionsList';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

interface SelectedAccountService {
  Name: string;
}

interface SelectedAccount {
  Services?: SelectedAccountService[];
}

interface FaqQuestionItem {
  title: string;
  icon?: React.ReactNode;
  answer: React.ReactNode;
  tour?: boolean;
  tourKey?: string;
  tourPath?: string;
  tourLabel?: string;
}

interface FaqSubsection {
  title: string;
  questions: FaqQuestionItem[];
}

interface FaqSection {
  title: string;
  questions?: FaqQuestionItem[];
  subsections?: FaqSubsection[];
  manualLink?: string;
}

interface FaqService {
  services: string[];
  title: string;
  icon?: React.ReactNode;
  manualLink?: string;
  sections?: FaqSection[];
  questions?: FaqQuestionItem[];
}

type SupportTab =
  | 'PVO'
  | 'ENERGY_XPLORER'
  | 'DATAHUB_INTEGRATION'
  | 'DEVICES'
  | 'API'
  | 'ALARMS'
  | 'ANALYTICS'
  | 'GENERAL';

const tabsWithoutServiceRequirement: SupportTab[] = [
  'ANALYTICS',
  'DEVICES',
  'ALARMS',
  'API',
  'GENERAL',
];

const supportTabs: Array<{ key: SupportTab; label: string }> = [
  // { key: 'GENERAL', label: 'General' },
  { key: 'DEVICES', label: 'Devices' },
  { key: 'ANALYTICS', label: 'Analytics' },
  // { key: 'ALARMS', label: 'Alarms' },
  { key: 'PVO', label: 'Predictive Heat Control' },
  { key: 'ENERGY_XPLORER', label: 'EnergyXplorer' },
  { key: 'DATAHUB_INTEGRATION', label: 'DataHub-integration' },
  // { key: 'API', label: 'API' },
];

const RouteComponent = () => {
  const { t } = useTranslation('support');
  const { isAdmin } = useSessionAuth();
  const selectedAccount = useSelector(getSelectedAccount) as
    SelectedAccount | undefined;

  const hasService = (service: string): boolean =>
    selectedAccount?.Services?.some((s) => s.Name === service) || isAdmin;

  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [openQuestionSection, setOpenQuestionSection] = React.useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = React.useState<SupportTab>('DEVICES');

  const toggleSection = (section: string): void => {
    setOpenQuestionSection(openQuestionSection === section ? null : section);
    setExpanded(null);
  };

  const handleChange = (panel: string): void => {
    setExpanded(expanded === panel ? null : panel);
  };

  const typedQuestionsList = QuestionsList as FaqService[];
  const accountFilteredList = typedQuestionsList.filter((s) =>
    s.services.some((service) => hasService(service)),
  );

  const shouldBypassServiceFiltering =
    tabsWithoutServiceRequirement.includes(activeTab);
  const sourceList = shouldBypassServiceFiltering
    ? typedQuestionsList
    : accountFilteredList;

  const tabFilteredList = sourceList.filter((service) => {
    if (activeTab === 'PVO') {
      return service.services.includes('PVO');
    }
    if (activeTab === 'ENERGY_XPLORER') {
      return service.services.includes('EnergyXplorer');
    }
    if (activeTab === 'DEVICES') {
      return service.services.includes('Devices');
    }
    if (activeTab === 'ANALYTICS') {
      return service.services.includes('Analytics');
    }
    return service.services.includes('DatahubIntegration');
  });

  const handleTabChange = (tab: SupportTab): void => {
    setActiveTab(tab);
    setExpanded(null);
    setOpenQuestionSection(null);
  };

  return (
    <Suspense fallback={<CircularProgress />}>
      <Container
        id="faq"
        maxWidth="lg"
        sx={{
          py: 8,
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            width: '100%',
            textAlign: 'center',
          }}
        >
          {t('title')}
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{
            width: '100%',
            textAlign: 'center',
            mb: 2,
          }}
        >
          {t('subtitle')}
        </Typography>

        <Stack justifyContent="center" textAlign={'center'} alignItems="center">
          <ListItem
            disablePadding
            sx={{ justifyContent: 'center', width: 'auto' }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
              <PhoneRoundedIcon />
            </ListItemIcon>
            <ListItemText sx={{ flex: 'none' }}>
              <Typography variant="subtitle1">{t('contact.phone')}</Typography>
            </ListItemText>
          </ListItem>

          <ListItem
            disablePadding
            sx={{ justifyContent: 'center', width: 'auto' }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
              <EmailRoundedIcon />
            </ListItemIcon>
            <ListItemText sx={{ flex: 'none' }}>
              <Typography variant="subtitle1">
                <a href={`mailto:${t('contact.email')}`}>
                  {t('contact.email')}
                </a>
              </Typography>
            </ListItemText>
          </ListItem>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            my: 4,
            flexWrap: 'wrap',
            rowGap: 1,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {supportTabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <Chip
                key={tab.key}
                label={tab.label}
                clickable
                color={isActive ? 'primary' : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                onClick={() => handleTabChange(tab.key)}
                sx={{ p: 2.5, borderRadius: 999, fontSize: '1rem' }}
              />
            );
          })}
        </Stack>

        {tabFilteredList.length > 0 ? (
          <Stack width={'100%'} gap={2}>
            {tabFilteredList.map((service, index) => (
              <React.Fragment key={service.services.join('_')}>
                <Suspense fallback={<Skeleton />}>
                  <FaqServiceSectionWrapper
                    key={service.services.join('_')}
                    title={service.title}
                    icon={service.icon}
                    manualLink={service.manualLink}
                  >
                    {service.sections?.map((section, index) => {
                      const sectionId = `section_${service.services.join('_')}_${index + 1}`;
                      // Handle sections with subsections (like EnergyXplorer)
                      if (section.subsections) {
                        return (
                          <Box key={section.title} sx={{ mb: 3 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              position="relative"
                              sx={{ mb: 2, mt: 2 }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 400,
                                  color: 'primary.main',
                                }}
                              >
                                {section.title}
                              </Typography>
                              {section.manualLink && (
                                <Button
                                  component="a"
                                  variant="outlined"
                                  href={section.manualLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  startIcon={<MenuBookIcon />}
                                  sx={{ position: 'absolute', right: 0 }}
                                >
                                  {t('manual')}
                                </Button>
                              )}
                            </Box>
                            {section.subsections.map((subsection, subIndex) => {
                              const subsectionId = `subsection_${service.services.join('_')}_${index + 1}_${subIndex + 1}`;
                              return (
                                <Suspense
                                  key={subsection.title}
                                  fallback={<Skeleton />}
                                >
                                  <FaqQuestionSectionWrapper
                                    key={subsection.title}
                                    title={subsection.title}
                                    id={subsectionId}
                                    toggleSection={toggleSection}
                                    expanded={
                                      openQuestionSection === subsectionId
                                    }
                                  >
                                    <Suspense fallback={<Skeleton />}>
                                      {subsection.questions.map((q, idx) => {
                                        const id = `question_${index + 1}_${subIndex + 1}_${idx + 1}`;

                                        return (
                                          <FaqQuestion
                                            key={id}
                                            id={id}
                                            expanded={expanded === id}
                                            handleChange={handleChange}
                                            icon={q.icon}
                                            title={q.title}
                                            details={q.answer}
                                            tour={q.tour}
                                            tourKey={q.tourKey}
                                            tourPath={q.tourPath}
                                            tourLabel={q.tourLabel}
                                          />
                                        );
                                      })}
                                    </Suspense>
                                  </FaqQuestionSectionWrapper>
                                </Suspense>
                              );
                            })}
                          </Box>
                        );
                      }

                      // Handle regular sections with questions directly
                      return (
                        <Suspense key={section.title} fallback={<Skeleton />}>
                          <FaqQuestionSectionWrapper
                            key={section.title}
                            title={section.title}
                            id={sectionId}
                            toggleSection={toggleSection}
                            expanded={openQuestionSection === sectionId}
                          >
                            <Suspense fallback={<Skeleton />}>
                              {section.questions.map((q, idx) => {
                                const id = `question_${index + 1}_${idx + 1}`;
                                return (
                                  <FaqQuestion
                                    key={id}
                                    id={id}
                                    icon={q.icon}
                                    expanded={expanded === id}
                                    handleChange={handleChange}
                                    title={q.title}
                                    details={q.answer}
                                    tour={q.tour}
                                    tourKey={q.tourKey}
                                    tourPath={q.tourPath}
                                    tourLabel={q.tourLabel}
                                  />
                                );
                              })}
                            </Suspense>
                          </FaqQuestionSectionWrapper>
                        </Suspense>
                      );
                    })}

                    {service.questions?.map((q, idx) => {
                      const id = `service_question_${index + 1}_${idx + 1}`;
                      return (
                        <FaqQuestion
                          key={id}
                          id={id}
                          icon={q.icon}
                          expanded={expanded === id}
                          handleChange={handleChange}
                          title={q.title}
                          details={q.answer}
                          tour={q.tour}
                          tourKey={q.tourKey}
                          tourPath={q.tourPath}
                          tourLabel={q.tourLabel}
                        />
                      );
                    })}
                  </FaqServiceSectionWrapper>
                </Suspense>
              </React.Fragment>
            ))}
          </Stack>
        ) : (
          <Alert severity="info">
            <Typography>{t('intro')}</Typography>
          </Alert>
        )}
      </Container>
    </Suspense>
  );
};

export const Route = createFileRoute('/$accountId/support')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'glossary:support' },
});
